import { useState, useEffect, useRef } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  getSiteSettings,
  updateSiteSettings,
  createSiteSettings,
  getBusinessSectors,
  createBusinessSector,
  updateBusinessSector,
  deleteBusinessSector,
  getFaqs,
  createFaq,
  updateFaq,
  deleteFaq,
  getTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
  getPartners,
  createPartner,
  updatePartner,
  deletePartner,
  SiteSettings,
  BusinessSector,
  BusinessSectorInsert,
  Faq,
  FaqInsert,
  Testimonial,
  TestimonialInsert,
  Partner,
  PartnerInsert,
} from '@/services/content'
import { uploadImage } from '@/services/storage'
import { BusinessAreaDialog } from '@/components/admin/BusinessAreaDialog'
import { FaqDialog } from '@/components/admin/FaqDialog'
import { TestimonialDialog } from '@/components/admin/TestimonialDialog'
import { PartnerDialog } from '@/components/admin/PartnerDialog'
import { iconMap } from '@/lib/icons'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useToast } from '@/hooks/use-toast'
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Save,
  Globe,
  Star,
  User,
  ExternalLink,
  Facebook,
  Linkedin,
  Instagram,
  MapPin,
  Phone,
  Upload,
  Link as LinkIcon,
  Mail,
  MessageCircle,
} from 'lucide-react'
import { Locale } from '@/stores/languageStore'
import { RichTextEditor } from '@/components/admin/RichTextEditor'
import { DashboardMockup } from '@/components/landing/DashboardMockup'

const settingsSchema = z.object({
  hero_title: z.string().min(1, 'Title is required'),
  hero_description: z.string().min(1, 'Description is required'),
  hero_cta_text: z.string().min(1, 'Button text is required'),
  hero_image_url: z.string().nullable().optional(),
  contact_email: z.string().email('Invalid email'),
  footer_text: z.string().min(1, 'Footer text is required'),
  explainer_video_url: z.string().nullable().optional(),
  explainer_video_title: z.string().nullable().optional(),
  company_logo_url: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  phone_number: z.string().nullable().optional(),
  linkedin_url: z
    .string()
    .url('Invalid URL')
    .nullable()
    .optional()
    .or(z.literal('')),
  instagram_url: z
    .string()
    .url('Invalid URL')
    .nullable()
    .optional()
    .or(z.literal('')),
  facebook_url: z
    .string()
    .url('Invalid URL')
    .nullable()
    .optional()
    .or(z.literal('')),
  // Chatbot Settings
  chatbot_primary_color: z.string().optional(),
  chatbot_secondary_color: z.string().optional(),
  chatbot_cta_config: z
    .array(
      z.object({
        label: z.string().min(1, 'Required'),
        url: z.string().url('Invalid URL'),
      }),
    )
    .optional(),
})

const ContentEditor = () => {
  const [adminLocale, setAdminLocale] = useState<Locale>('pt')
  const [settings, setSettings] = useState<SiteSettings | null>(null)
  const [sectors, setSectors] = useState<BusinessSector[]>([])
  const [faqs, setFaqs] = useState<Faq[]>([])
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [partners, setPartners] = useState<Partner[]>([])
  const [loading, setLoading] = useState(true)
  const [savingSettings, setSavingSettings] = useState(false)

  const [isUploadingHero, setIsUploadingHero] = useState(false)
  const heroFileInputRef = useRef<HTMLInputElement>(null)

  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const logoFileInputRef = useRef<HTMLInputElement>(null)

  // Dialog States
  const [isSectorDialogOpen, setIsSectorDialogOpen] = useState(false)
  const [selectedSector, setSelectedSector] = useState<BusinessSector | null>(
    null,
  )
  const [isSavingSector, setIsSavingSector] = useState(false)

  const [isFaqDialogOpen, setIsFaqDialogOpen] = useState(false)
  const [selectedFaq, setSelectedFaq] = useState<Faq | null>(null)
  const [isSavingFaq, setIsSavingFaq] = useState(false)

  const [isTestimonialDialogOpen, setIsTestimonialDialogOpen] = useState(false)
  const [selectedTestimonial, setSelectedTestimonial] =
    useState<Testimonial | null>(null)
  const [isSavingTestimonial, setIsSavingTestimonial] = useState(false)

  const [isPartnerDialogOpen, setIsPartnerDialogOpen] = useState(false)
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null)
  const [isSavingPartner, setIsSavingPartner] = useState(false)

  const { toast } = useToast()

  const settingsForm = useForm<z.infer<typeof settingsSchema>>({
    resolver: zodResolver(settingsSchema),
  })

  // Field Array for Chatbot CTAs
  const {
    fields: ctaFields,
    append: appendCta,
    remove: removeCta,
  } = useFieldArray({
    control: settingsForm.control,
    name: 'chatbot_cta_config',
  })

  useEffect(() => {
    fetchData()
  }, [adminLocale])

  const fetchData = async () => {
    setLoading(true)
    try {
      // Don't use fallback in admin, we want to know if it exists
      const [settingsRes, sectorsRes, faqsRes, testimonialsRes, partnersRes] =
        await Promise.all([
          getSiteSettings(adminLocale, false),
          getBusinessSectors(adminLocale, false),
          getFaqs(adminLocale, false),
          getTestimonials(adminLocale, false),
          getPartners(), // Partners are global
        ])

      if (settingsRes.data) {
        setSettings(settingsRes.data)
        settingsForm.reset({
          hero_title: settingsRes.data.hero_title,
          hero_description: settingsRes.data.hero_description,
          hero_cta_text: settingsRes.data.hero_cta_text,
          hero_image_url: settingsRes.data.hero_image_url,
          contact_email: settingsRes.data.contact_email,
          footer_text: settingsRes.data.footer_text,
          explainer_video_url: settingsRes.data.explainer_video_url,
          explainer_video_title: settingsRes.data.explainer_video_title,
          company_logo_url: settingsRes.data.company_logo_url,
          address: settingsRes.data.address,
          phone_number: settingsRes.data.phone_number,
          linkedin_url: settingsRes.data.linkedin_url,
          instagram_url: settingsRes.data.instagram_url,
          facebook_url: settingsRes.data.facebook_url,
          chatbot_primary_color:
            settingsRes.data.chatbot_primary_color || '#4F46E5',
          chatbot_secondary_color:
            settingsRes.data.chatbot_secondary_color || '#E0E7FF',
          chatbot_cta_config: settingsRes.data.chatbot_cta_config || [],
        })
      } else {
        setSettings(null)
        // Reset form to empty if no settings found for this locale
        settingsForm.reset({
          hero_title: '',
          hero_description: '',
          hero_cta_text: '',
          hero_image_url: null,
          contact_email: '',
          footer_text: '',
          explainer_video_url: '',
          explainer_video_title: '',
          company_logo_url: '',
          address: '',
          phone_number: '',
          linkedin_url: '',
          instagram_url: '',
          facebook_url: '',
          chatbot_primary_color: '#4F46E5',
          chatbot_secondary_color: '#E0E7FF',
          chatbot_cta_config: [],
        })
      }

      setSectors(sectorsRes.data || [])
      setFaqs(faqsRes.data || [])
      setTestimonials(testimonialsRes.data || [])
      setPartners(partnersRes.data || [])
    } catch (error) {
      console.error('Error loading content:', error)
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load data.',
      })
    } finally {
      setLoading(false)
    }
  }

  const onSaveSettings = async (values: z.infer<typeof settingsSchema>) => {
    setSavingSettings(true)
    try {
      if (settings?.id) {
        // Update existing
        const { error } = await updateSiteSettings({
          id: settings.id,
          ...values,
          locale: adminLocale, // Ensure locale is correct
        })
        if (error) throw error
      } else {
        // Create new
        const { error } = await createSiteSettings({
          ...values,
          locale: adminLocale,
        })
        if (error) throw error
      }

      toast({
        title: 'Success',
        description: 'Company information updated successfully',
      })
      // Refresh to get the ID if we just created it
      fetchData()
    } catch (error) {
      // Sanitize error logging
      console.error(
        'Error saving settings:',
        error instanceof Error ? error.message : error,
      )
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to save changes.',
      })
    } finally {
      setSavingSettings(false)
    }
  }

  const handleHeroImageSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate type
    if (!file.type.startsWith('image/')) {
      toast({
        variant: 'destructive',
        title: 'Invalid file type',
        description: 'Please upload an image file.',
      })
      return
    }

    // Validate size (e.g. 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: 'destructive',
        title: 'File too large',
        description: 'Maximum file size is 5MB.',
      })
      return
    }

    try {
      setIsUploadingHero(true)
      const url = await uploadImage(file, 'images')
      settingsForm.setValue('hero_image_url', url, { shouldDirty: true })
      toast({
        title: 'Image uploaded',
        description: "Don't forget to save changes.",
      })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      console.error('Upload failed:', errorMessage)
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: errorMessage,
      })
    } finally {
      setIsUploadingHero(false)
      if (heroFileInputRef.current) {
        heroFileInputRef.current.value = ''
      }
    }
  }

  const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast({
        variant: 'destructive',
        title: 'Invalid file type',
        description: 'Please upload an image file.',
      })
      return
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({
        variant: 'destructive',
        title: 'File too large',
        description: 'Maximum file size is 2MB for logos.',
      })
      return
    }

    try {
      setIsUploadingLogo(true)
      const url = await uploadImage(file, 'images')
      settingsForm.setValue('company_logo_url', url, { shouldDirty: true })
      toast({
        title: 'Logo uploaded',
        description: "Don't forget to save changes.",
      })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      console.error('Upload failed:', errorMessage)
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: errorMessage,
      })
    } finally {
      setIsUploadingLogo(false)
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = ''
      }
    }
  }

  // --- CRUD Handlers for Sectors ---
  const handleCreateOrUpdateSector = async (values: BusinessSectorInsert) => {
    setIsSavingSector(true)
    try {
      if (selectedSector) {
        const { error } = await updateBusinessSector(selectedSector.id, {
          ...values,
          locale: adminLocale,
        })
        if (error) throw error
        toast({ title: 'Sector updated successfully' })
      } else {
        const { error } = await createBusinessSector({
          ...values,
          locale: adminLocale,
        })
        if (error) throw error
        toast({ title: 'Sector created successfully' })
      }

      const { data } = await getBusinessSectors(adminLocale, false)
      if (data) setSectors(data)
      setIsSectorDialogOpen(false)
    } catch (error) {
      console.error(
        'Error saving sector:',
        error instanceof Error ? error.message : error,
      )
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to save sector.',
      })
    } finally {
      setIsSavingSector(false)
    }
  }

  const handleDeleteSector = async (id: string) => {
    if (!confirm('Are you sure you want to delete this business area?')) return
    try {
      const { error } = await deleteBusinessSector(id)
      if (error) throw error
      setSectors(sectors.filter((s) => s.id !== id))
      toast({ title: 'Sector deleted' })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete sector.',
      })
    }
  }

  // --- CRUD Handlers for FAQs ---
  const handleCreateOrUpdateFaq = async (values: FaqInsert) => {
    setIsSavingFaq(true)
    try {
      if (selectedFaq) {
        const { error } = await updateFaq(selectedFaq.id, {
          ...values,
          locale: adminLocale,
        })
        if (error) throw error
        toast({ title: 'FAQ updated successfully' })
      } else {
        const { error } = await createFaq({ ...values, locale: adminLocale })
        if (error) throw error
        toast({ title: 'FAQ created successfully' })
      }

      const { data } = await getFaqs(adminLocale, false)
      if (data) setFaqs(data)
      setIsFaqDialogOpen(false)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to save FAQ.',
      })
    } finally {
      setIsSavingFaq(false)
    }
  }

  const handleDeleteFaq = async (id: string) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return
    try {
      const { error } = await deleteFaq(id)
      if (error) throw error
      setFaqs(faqs.filter((f) => f.id !== id))
      toast({ title: 'FAQ deleted' })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete FAQ.',
      })
    }
  }

  // --- CRUD Handlers for Testimonials ---
  const handleCreateOrUpdateTestimonial = async (values: TestimonialInsert) => {
    setIsSavingTestimonial(true)
    try {
      if (selectedTestimonial) {
        const { error } = await updateTestimonial(selectedTestimonial.id, {
          ...values,
          locale: adminLocale,
        })
        if (error) throw error
        toast({ title: 'Testimonial updated successfully' })
      } else {
        const { error } = await createTestimonial({
          ...values,
          locale: adminLocale,
        })
        if (error) throw error
        toast({ title: 'Testimonial created successfully' })
      }

      const { data } = await getTestimonials(adminLocale, false)
      if (data) setTestimonials(data)
      setIsTestimonialDialogOpen(false)
    } catch (error) {
      console.error(error)
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to save testimonial.',
      })
    } finally {
      setIsSavingTestimonial(false)
    }
  }

  const handleDeleteTestimonial = async (id: string) => {
    if (!confirm('Are you sure you want to delete this testimonial?')) return
    try {
      const { error } = await deleteTestimonial(id)
      if (error) throw error
      setTestimonials(testimonials.filter((t) => t.id !== id))
      toast({ title: 'Testimonial deleted' })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete testimonial.',
      })
    }
  }

  // --- CRUD Handlers for Partners ---
  const handleCreateOrUpdatePartner = async (values: PartnerInsert) => {
    setIsSavingPartner(true)
    try {
      if (selectedPartner) {
        const { error } = await updatePartner(selectedPartner.id, values)
        if (error) throw error
        toast({ title: 'Partner updated successfully' })
      } else {
        const { error } = await createPartner(values)
        if (error) throw error
        toast({ title: 'Partner created successfully' })
      }

      const { data } = await getPartners()
      if (data) setPartners(data)
      setIsPartnerDialogOpen(false)
    } catch (error) {
      console.error(error)
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to save partner.',
      })
    } finally {
      setIsSavingPartner(false)
    }
  }

  const handleDeletePartner = async (id: string) => {
    if (!confirm('Are you sure you want to delete this partner?')) return
    try {
      const { error } = await deletePartner(id)
      if (error) throw error
      setPartners(partners.filter((p) => p.id !== id))
      toast({ title: 'Partner deleted' })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete partner.',
      })
    }
  }

  // --- Dialog Controls ---
  const openNewSectorDialog = () => {
    setSelectedSector(null)
    setIsSectorDialogOpen(true)
  }
  const openEditSectorDialog = (sector: BusinessSector) => {
    setSelectedSector(sector)
    setIsSectorDialogOpen(true)
  }

  const openNewFaqDialog = () => {
    setSelectedFaq(null)
    setIsFaqDialogOpen(true)
  }
  const openEditFaqDialog = (faq: Faq) => {
    setSelectedFaq(faq)
    setIsFaqDialogOpen(true)
  }

  const openNewTestimonialDialog = () => {
    setSelectedTestimonial(null)
    setIsTestimonialDialogOpen(true)
  }
  const openEditTestimonialDialog = (t: Testimonial) => {
    setSelectedTestimonial(t)
    setIsTestimonialDialogOpen(true)
  }

  const openNewPartnerDialog = () => {
    setSelectedPartner(null)
    setIsPartnerDialogOpen(true)
  }
  const openEditPartnerDialog = (p: Partner) => {
    setSelectedPartner(p)
    setIsPartnerDialogOpen(true)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            Content Management
          </h1>
          <p className="text-slate-500 mt-2">
            Edit landing page text and sections.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-700">Language:</span>
          <Select
            value={adminLocale}
            onValueChange={(v) => setAdminLocale(v as Locale)}
          >
            <SelectTrigger className="w-[180px]">
              <Globe className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pt">Português (PT)</SelectItem>
              <SelectItem value="en">English (EN)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="flex flex-wrap md:grid w-full grid-cols-2 md:grid-cols-7 max-w-[900px] gap-2 h-auto md:h-10">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="company-info">Company Info</TabsTrigger>
            <TabsTrigger value="chatbot">Chatbot</TabsTrigger>
            <TabsTrigger value="sectors">Solutions</TabsTrigger>
            <TabsTrigger value="partners">Partners</TabsTrigger>
            <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="faqs">FAQs</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>
                  Global Settings ({adminLocale.toUpperCase()})
                </CardTitle>
                <CardDescription>
                  Configure Hero section, main CTA and footer.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...settingsForm}>
                  <form
                    onSubmit={settingsForm.handleSubmit(onSaveSettings)}
                    className="space-y-6"
                  >
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                        Hero Section
                      </h3>

                      <div className="grid grid-cols-1 gap-6">
                        <div className="space-y-4">
                          <FormField
                            control={settingsForm.control}
                            name="hero_title"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Main Title</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={settingsForm.control}
                            name="hero_description"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Subtitle / Description</FormLabel>
                                <FormControl>
                                  <RichTextEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder="Enter hero description..."
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={settingsForm.control}
                            name="hero_cta_text"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Button Text (CTA)</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="space-y-2">
                          <FormField
                            control={settingsForm.control}
                            name="hero_image_url"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Hero Dashboard Image</FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <input
                                      type="file"
                                      ref={heroFileInputRef}
                                      onChange={handleHeroImageSelect}
                                      className="hidden"
                                      accept="image/*"
                                    />
                                    <DashboardMockup
                                      imageUrl={field.value}
                                      onUploadClick={() =>
                                        heroFileInputRef.current?.click()
                                      }
                                      isUploading={isUploadingHero}
                                      className="h-[400px]"
                                    />
                                  </div>
                                </FormControl>

                                <div className="flex items-center gap-2 mt-3">
                                  <LinkIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                  <Input
                                    value={field.value || ''}
                                    onChange={(e) =>
                                      field.onChange(e.target.value || null)
                                    }
                                    placeholder="Or paste image URL here..."
                                    className="h-9 text-xs font-mono"
                                  />
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">
                                  Click on the dashboard to upload a new image,
                                  or paste a URL above. Recommended size:
                                  1200x800px.
                                </p>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 pt-4 border-t">
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                        Video Section
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={settingsForm.control}
                          name="explainer_video_title"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Video Section Title</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="How it Works"
                                  {...field}
                                  value={field.value || ''}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={settingsForm.control}
                          name="explainer_video_url"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Explainer Video URL</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="https://youtube.com/..."
                                  {...field}
                                  value={field.value || ''}
                                />
                              </FormControl>
                              <p className="text-xs text-muted-foreground">
                                YouTube or Vimeo URL. Leave empty to hide
                                section.
                              </p>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="space-y-4 pt-4 border-t">
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                        Footer
                      </h3>
                      <FormField
                        control={settingsForm.control}
                        name="footer_text"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Footer Text</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex justify-end">
                      <Button type="submit" disabled={savingSettings}>
                        {savingSettings ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="mr-2 h-4 w-4" />
                        )}
                        Save Changes
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="company-info" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>
                  Company Information ({adminLocale.toUpperCase()})
                </CardTitle>
                <CardDescription>
                  Manage business identity, contacts, and social media links.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...settingsForm}>
                  <form
                    onSubmit={settingsForm.handleSubmit(onSaveSettings)}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                          Business Identity
                        </h3>

                        <FormField
                          control={settingsForm.control}
                          name="company_logo_url"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Company Logo URL</FormLabel>
                              <div className="flex gap-2">
                                <FormControl>
                                  <Input
                                    placeholder="https://..."
                                    {...field}
                                    value={field.value || ''}
                                  />
                                </FormControl>
                                <input
                                  type="file"
                                  ref={logoFileInputRef}
                                  onChange={handleLogoSelect}
                                  className="hidden"
                                  accept="image/*"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  onClick={() =>
                                    logoFileInputRef.current?.click()
                                  }
                                  disabled={isUploadingLogo}
                                >
                                  {isUploadingLogo ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Upload className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                              {field.value && (
                                <div className="mt-2 p-2 border rounded-md w-fit bg-slate-50">
                                  <img
                                    src={field.value}
                                    alt="Logo Preview"
                                    className="h-12 object-contain"
                                  />
                                </div>
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={settingsForm.control}
                          name="contact_email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Contact Email</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                  <Input
                                    className="pl-9"
                                    placeholder="hello@company.com"
                                    {...field}
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={settingsForm.control}
                          name="phone_number"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Phone Number</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                  <Input
                                    className="pl-9"
                                    placeholder="+1 234 567 890"
                                    {...field}
                                    value={field.value || ''}
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="space-y-4">
                        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                          Location
                        </h3>
                        <FormField
                          control={settingsForm.control}
                          name="address"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Physical Address</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                  <Textarea
                                    className="pl-9 min-h-[120px]"
                                    placeholder="123 Business St, City, Country"
                                    {...field}
                                    value={field.value || ''}
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="space-y-4 pt-4 border-t">
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                        Social Media
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={settingsForm.control}
                          name="linkedin_url"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>LinkedIn URL</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Linkedin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                  <Input
                                    className="pl-9"
                                    placeholder="https://linkedin.com/in/..."
                                    {...field}
                                    value={field.value || ''}
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={settingsForm.control}
                          name="instagram_url"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Instagram URL</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Instagram className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                  <Input
                                    className="pl-9"
                                    placeholder="https://instagram.com/..."
                                    {...field}
                                    value={field.value || ''}
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={settingsForm.control}
                          name="facebook_url"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Facebook URL</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Facebook className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                  <Input
                                    className="pl-9"
                                    placeholder="https://facebook.com/..."
                                    {...field}
                                    value={field.value || ''}
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button type="submit" disabled={savingSettings}>
                        {savingSettings ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="mr-2 h-4 w-4" />
                        )}
                        Save Changes
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="chatbot" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Chatbot Customization</CardTitle>
                <CardDescription>
                  Customize the appearance and behavior of your AI assistant.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...settingsForm}>
                  <form
                    onSubmit={settingsForm.handleSubmit(onSaveSettings)}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                          Appearance
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={settingsForm.control}
                            name="chatbot_primary_color"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Primary Color</FormLabel>
                                <div className="flex gap-2">
                                  <Input
                                    type="color"
                                    className="w-12 p-1 h-10"
                                    {...field}
                                  />
                                  <Input
                                    {...field}
                                    placeholder="#000000"
                                    className="flex-1"
                                  />
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={settingsForm.control}
                            name="chatbot_secondary_color"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Secondary Color</FormLabel>
                                <div className="flex gap-2">
                                  <Input
                                    type="color"
                                    className="w-12 p-1 h-10"
                                    {...field}
                                  />
                                  <Input
                                    {...field}
                                    placeholder="#ffffff"
                                    className="flex-1"
                                  />
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className="p-4 border rounded-lg bg-slate-50">
                          <p className="text-sm font-medium mb-2">Preview</p>
                          <div className="flex gap-2 items-center">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white"
                              style={{
                                backgroundColor:
                                  settingsForm.watch('chatbot_primary_color') ||
                                  '#4F46E5',
                              }}
                            >
                              <MessageCircle size={16} />
                            </div>
                            <div className="bg-white px-3 py-2 rounded-lg border text-sm">
                              Hello! How can I help?
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                            Action Widgets
                          </h3>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => appendCta({ label: '', url: '' })}
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add CTA
                          </Button>
                        </div>

                        <div className="space-y-3">
                          {ctaFields.map((field, index) => (
                            <div
                              key={field.id}
                              className="flex gap-2 items-start p-3 border rounded-md bg-slate-50"
                            >
                              <div className="flex-1 grid gap-2">
                                <FormField
                                  control={settingsForm.control}
                                  name={`chatbot_cta_config.${index}.label`}
                                  render={({ field }) => (
                                    <FormItem className="space-y-1">
                                      <FormLabel className="text-xs">
                                        Label
                                      </FormLabel>
                                      <FormControl>
                                        <Input
                                          {...field}
                                          placeholder="e.g. Book Demo"
                                          className="h-8"
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={settingsForm.control}
                                  name={`chatbot_cta_config.${index}.url`}
                                  render={({ field }) => (
                                    <FormItem className="space-y-1">
                                      <FormLabel className="text-xs">
                                        URL
                                      </FormLabel>
                                      <FormControl>
                                        <Input
                                          {...field}
                                          placeholder="https://..."
                                          className="h-8"
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-6"
                                onClick={() => removeCta(index)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          {ctaFields.length === 0 && (
                            <div className="text-center py-8 text-slate-400 text-sm border border-dashed rounded-md">
                              No action widgets configured.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t">
                      <Button type="submit" disabled={savingSettings}>
                        {savingSettings ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="mr-2 h-4 w-4" />
                        )}
                        Save Chatbot Settings
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sectors" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="space-y-1">
                  <CardTitle>
                    Business Areas ({adminLocale.toUpperCase()})
                  </CardTitle>
                  <CardDescription>
                    Manage cards displayed in the "Solutions" section.
                  </CardDescription>
                </div>
                <Button onClick={openNewSectorDialog} size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  Add New
                </Button>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border mt-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[80px]">Order</TableHead>
                        <TableHead>Icon/Image</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead className="hidden md:table-cell">
                          Slug
                        </TableHead>
                        <TableHead className="hidden lg:table-cell">
                          Description
                        </TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sectors.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="text-center py-8 text-muted-foreground"
                          >
                            No business areas found for this language.
                          </TableCell>
                        </TableRow>
                      ) : (
                        sectors.map((sector) => {
                          const Icon =
                            iconMap[sector.icon_name] || iconMap['Globe']
                          return (
                            <TableRow key={sector.id}>
                              <TableCell>{sector.sort_order}</TableCell>
                              <TableCell>
                                <div className="flex items-center justify-center w-10 h-10 bg-slate-100 rounded-md overflow-hidden">
                                  {sector.image_url ? (
                                    <img
                                      src={sector.image_url}
                                      alt={sector.title}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <Icon className="h-5 w-5 text-slate-700" />
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="font-medium">
                                {sector.title}
                              </TableCell>
                              <TableCell className="hidden md:table-cell text-sm text-slate-500 font-mono">
                                {sector.slug || '-'}
                              </TableCell>
                              <TableCell className="hidden lg:table-cell text-muted-foreground max-w-md truncate">
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: sector.description,
                                  }}
                                  className="line-clamp-1"
                                />
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openEditSectorDialog(sector)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                    onClick={() =>
                                      handleDeleteSector(sector.id)
                                    }
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="partners" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="space-y-1">
                  <CardTitle>Partners & Clients</CardTitle>
                  <CardDescription>
                    Manage logos of partners displayed on the landing page
                    (Global).
                  </CardDescription>
                </div>
                <Button onClick={openNewPartnerDialog} size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  Add New
                </Button>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border mt-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[80px]">Order</TableHead>
                        <TableHead>Logo</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead className="hidden md:table-cell">
                          Website
                        </TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partners.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="text-center py-8 text-muted-foreground"
                          >
                            No partners found.
                          </TableCell>
                        </TableRow>
                      ) : (
                        partners.map((partner) => (
                          <TableRow key={partner.id}>
                            <TableCell>{partner.sort_order}</TableCell>
                            <TableCell>
                              <div className="w-16 h-10 bg-slate-50 rounded border flex items-center justify-center p-1">
                                <img
                                  src={partner.logo_url}
                                  alt={partner.name}
                                  className="max-w-full max-h-full object-contain"
                                />
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">
                              {partner.name}
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-muted-foreground">
                              {partner.website_url ? (
                                <a
                                  href={partner.website_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center hover:text-primary"
                                >
                                  {partner.website_url}
                                  <ExternalLink className="w-3 h-3 ml-1" />
                                </a>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditPartnerDialog(partner)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                  onClick={() =>
                                    handleDeletePartner(partner.id)
                                  }
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="testimonials" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="space-y-1">
                  <CardTitle>
                    Testimonials ({adminLocale.toUpperCase()})
                  </CardTitle>
                  <CardDescription>
                    Manage customer testimonials and reviews.
                  </CardDescription>
                </div>
                <Button onClick={openNewTestimonialDialog} size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  Add New
                </Button>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border mt-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[80px]">Order</TableHead>
                        <TableHead>Author</TableHead>
                        <TableHead className="hidden sm:table-cell">
                          Role
                        </TableHead>
                        <TableHead>Rating</TableHead>
                        <TableHead className="hidden md:table-cell">
                          Content
                        </TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {testimonials.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="text-center py-8 text-muted-foreground"
                          >
                            No testimonials found for this language.
                          </TableCell>
                        </TableRow>
                      ) : (
                        testimonials.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.sort_order}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarImage
                                    src={item.author_avatar_url || ''}
                                  />
                                  <AvatarFallback>
                                    <User className="h-4 w-4" />
                                  </AvatarFallback>
                                </Avatar>
                                <span className="font-medium">
                                  {item.author_name}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell text-muted-foreground">
                              {item.author_role || '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <span className="text-sm font-medium">
                                  {item.rating}
                                </span>
                                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                              </div>
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-muted-foreground max-w-sm truncate">
                              {item.content}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    openEditTestimonialDialog(item)
                                  }
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                  onClick={() =>
                                    handleDeleteTestimonial(item.id)
                                  }
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="faqs" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="space-y-1">
                  <CardTitle>
                    Frequently Asked Questions ({adminLocale.toUpperCase()})
                  </CardTitle>
                  <CardDescription>
                    Manage FAQ items for{' '}
                    {adminLocale === 'pt' ? 'Portuguese' : 'English'}.
                  </CardDescription>
                </div>
                <Button onClick={openNewFaqDialog} size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  Add New
                </Button>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border mt-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[80px]">Order</TableHead>
                        <TableHead>Question</TableHead>
                        <TableHead className="hidden md:table-cell">
                          Answer
                        </TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {faqs.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            className="text-center py-8 text-muted-foreground"
                          >
                            No FAQs found for this language.
                          </TableCell>
                        </TableRow>
                      ) : (
                        faqs.map((faq) => (
                          <TableRow key={faq.id}>
                            <TableCell>{faq.sort_order}</TableCell>
                            <TableCell className="font-medium">
                              {faq.question}
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-muted-foreground max-w-md truncate">
                              <div
                                dangerouslySetInnerHTML={{ __html: faq.answer }}
                                className="line-clamp-1"
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditFaqDialog(faq)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                  onClick={() => handleDeleteFaq(faq.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      <BusinessAreaDialog
        open={isSectorDialogOpen}
        onOpenChange={setIsSectorDialogOpen}
        initialData={selectedSector}
        onSubmit={handleCreateOrUpdateSector}
        isSubmitting={isSavingSector}
        locale={adminLocale}
      />

      <TestimonialDialog
        open={isTestimonialDialogOpen}
        onOpenChange={setIsTestimonialDialogOpen}
        initialData={selectedTestimonial}
        onSubmit={handleCreateOrUpdateTestimonial}
        isSubmitting={isSavingTestimonial}
        locale={adminLocale}
      />

      <FaqDialog
        open={isFaqDialogOpen}
        onOpenChange={setIsFaqDialogOpen}
        initialData={selectedFaq}
        onSubmit={handleCreateOrUpdateFaq}
        isSubmitting={isSavingFaq}
        locale={adminLocale}
      />

      <PartnerDialog
        open={isPartnerDialogOpen}
        onOpenChange={setIsPartnerDialogOpen}
        initialData={selectedPartner}
        onSubmit={handleCreateOrUpdatePartner}
        isSubmitting={isSavingPartner}
      />
    </div>
  )
}

export default ContentEditor
